// Light/dark theme. index.html applies it before first paint; this module keeps
// <html data-theme> in sync after that. With no saved choice, Pulse follows the
// device setting (and changes with it); the toggle saves an explicit choice.
import { useEffect, useState } from "react";

export type Theme = "light" | "dark";
const KEY = "nsp.theme";
const THEME_COLOR: Record<Theme, string> = { light: "#F9F7F4", dark: "#0F1D22" };

function saved(): Theme | null {
  try {
    const t = localStorage.getItem(KEY);
    return t === "light" || t === "dark" ? t : null;
  } catch {
    return null;
  }
}

const system = (): Theme => (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light");

function apply(t: Theme): void {
  document.documentElement.dataset.theme = t;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[t]);
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => saved() ?? system());
  useEffect(() => apply(theme), [theme]);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const follow = () => !saved() && setTheme(system());
    mq.addEventListener("change", follow);
    return () => mq.removeEventListener("change", follow);
  }, []);
  const toggle = () =>
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(KEY, next);
      } catch {
        /* storage blocked: applies for this visit only */
      }
      return next;
    });
  return [theme, toggle];
}
